import { useEffect } from "react";
import {
  isReplyDraftStorageKey,
  purgeAllReplyDrafts,
} from "@/features/post/stores/replyDraftStore";
import {
  isThreadCreateDraftStorageKey,
  purgeAllThreadCreateDrafts,
} from "@/features/thread/stores/threadCreateDraftStore";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
} from "@/shared/ui/component-hierarchy/hooks";

type DraftStorageEvent = {
  type: "root.legacy-draft-written";
  key: string | null;
  value: string | null;
};
export function useLegacyDraftCleanupPresenter(): void {
  const mediator = useHierarchyResource<
    Mediator<"watching", DraftStorageEvent, "reply" | "thread">
  >((previous) =>
    createMediator<"watching", DraftStorageEvent, "reply" | "thread">(
      previous?.isDisposed
        ? (getHierarchyResourceSnapshot<"watching">(previous) ?? "watching")
        : "watching",
      (state, event) => {
        const effects: ("reply" | "thread")[] = [];
        if (event.value !== null) {
          if (isReplyDraftStorageKey(event.key)) effects.push("reply");
          if (isThreadCreateDraftStorageKey(event.key)) effects.push("thread");
        }
        return { status: "handled", state, effects };
      },
      {
        runEffect: (effect) => {
          if (effect === "reply") purgeAllReplyDrafts();
          else purgeAllThreadCreateDrafts();
        },
      },
    ),
  );
  const node = useComponentNode((event) => {
    if (event.type !== "root.legacy-draft-written") return "unhandled";
    mediator.dispatch(event as DraftStorageEvent);
    return "handled";
  });
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      node.dispatch({
        type: "root.legacy-draft-written",
        key: event.key,
        value: event.newValue,
      } as DraftStorageEvent);
    };
    window.addEventListener("storage", onStorage);
    return (): void => window.removeEventListener("storage", onStorage);
  }, [node]);
}
